import WasmController from "react-lib/frameworks/WasmController";

// Interface
import { State as MainState } from "../../../../../HIS/MainHISInterface";

// Common
import { SetProperty } from "../../common/CommonInterface";

// Serializer
import GenDrugContinueInterfaceLogSerializer from "issara-sdk/types/GenDrugContinueIssueLogSerializer_apps_TPD";

// API
import GenDrugContinueInterfaceLogList from "issara-sdk/apis/GenDrugContinueIssueLogList_apps_TPD";
import GenDrugContinueInterfaceData from "issara-sdk/apis/GenDrugContinueIssue_apps_TPD";

export type Props = {

}

export type State = Partial<{
  // seq
  GenerateContinueByDateSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    generateContinueData: GenerateContinueDataType;
    continueList: GenDrugContinueInterfaceLogSerializer[];
    paginatedItems: PaginatedItemsType;
  }>;
}>;

export type GenerateContinueDataType = Partial<{
  date: string;
}>;

type PaginatedItemsType = Partial<{
  items: GenDrugContinueInterfaceLogSerializer[];
  page: number;
  pageSize: number;
  total: number;
}>;

export type TableHeaderType<T> = {
  Header: string;
  accessor: keyof T;
  width?: number;
};

type PickedState = Partial<Pick<MainState, "buttonLoadCheck" | "errorMessage" | "successMessage">>;

export type PickedProps = Partial<Omit<PickedState, "">>;

// Sequence Type
type SeqState = {
  sequence: "GenerateContinueByDate";
  restart?: boolean;
  clear?: boolean;
};

// Action Type
type ActionType = 
  | { action: "FETCH"; }
  | { action: "CLEAR"; }
  | { action: "GENERATE"; };

type SeqAct = ActionType & SeqState;

type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => void;

export type SetProp = SetProperty<State & PickedState>;

type CustomExtract<T, U> = T extends object ? (U extends Partial<T> ? T : never) : never;

type Params<A extends ActionType["action"]> = CustomExtract<ActionType, { action: A }>;

export const StateInitial: State & PickedState = {
  // seq
  GenerateContinueByDateSequence: {
    sequenceIndex: null,
  },
  // SnackMessage
  successMessage: null,
  errorMessage: null,
};

export type Event = { message: "RunSequence"; params: {} };

export type Data = {
  division?: number;
};

type Handler<P = any, R = any> = (
  controller: WasmController<State & PickedState, Event, Data, Props>,
  params: P
) => R;

export const DataInitial = {};

export const CARD_NAME = "CardGenerateContinueByDate";

type Optional<T> = {
  [K in keyof T]: `${typeof CARD_NAME}_${T[K] & string}`;
};

export const ACTIONS = {
  GENERATE: "GENERATE",
  FETCH: "FETCH",
  CLEAR: "CLEAR",
} as const;

export const BTN_ACTS = Object.fromEntries(
  Object.keys(ACTIONS).map((key) => [key, `${CARD_NAME}_${key}`])
) as Optional<typeof ACTIONS>;

const INIT_DATA: GenerateContinueDataType = {
  date: "",
};

export const LIST_LIMIT = 20;

/**========================================================================
 **                               START
 *========================================================================**/

export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.setState(
    {
      GenerateContinueByDateSequence: {
        ...state.GenerateContinueByDateSequence,
        sequenceIndex: "Action",
        paginatedItems: {
          pageSize: LIST_LIMIT,
          page: 0,
        },
      },
    },
    () => Action(controller, { ...params, action: "FETCH" })
  );
};

/**========================================================================
 **                              ACTIONS
 *========================================================================**/

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers = {
    [ACTIONS.FETCH]: HandleFetchContinue,
    [ACTIONS.GENERATE]: HandleGenerateContinue,
    [ACTIONS.CLEAR]: HandleClearContinue,
  };

  const action = params.action;

  if (action && actionHandlers[action]) {
    return actionHandlers[action](controller, params as any);
  }
};

export const HandleFetchContinue: Handler<Params<"FETCH">> = async (controller, params) => {
  const state = controller.getState();

  const paginatedItems = state.GenerateContinueByDateSequence?.paginatedItems;
  const page = paginatedItems?.page || 0;
  const pageSize = paginatedItems?.pageSize || LIST_LIMIT;
  const offset = page * pageSize;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.FETCH]: "LOADING",
    }
  });

  let data = {
    limit: pageSize,
    offset,
  };

  const result = await GenDrugContinueLogData(controller, data);

  if (result?.items) {
    controller.setState({
      GenerateContinueByDateSequence: {
        ...state.GenerateContinueByDateSequence,
        paginatedItems: {
          ...paginatedItems,
          page,
          pageSize,
          total: result?.total,
        },
        continueList: result.items,
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.FETCH]: "SUCCESS",
      }
    });
  } else {
    controller.setState({
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: "Error To Get Log Data!",
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.FETCH]: "ERROR",
      }
    });
  }
};

export const HandleGenerateContinue: Handler<Params<"GENERATE">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.GENERATE]: "LOADING",
    },
  });

  const generateContinueData = state.GenerateContinueByDateSequence?.generateContinueData;

  const data = {
    generate_date: generateContinueData?.date,
  };

  const [genResp, genErr] = await GenerateDrugContinue(controller, { data });

  if (genResp) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.GENERATE]: "SUCCESS",
      },
      successMessage: {
        ...state.successMessage,
        [CARD_NAME]: genResp?.detail,
      },
    });

    // Clear Generate Drug Continue Data
    HandleClearContinue(controller, {} as any);
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.GENERATE]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: genErr,
      },
    });
  }
};

export const HandleClearContinue: Handler<Params<"CLEAR">> = async (controller, params) => {
  const state = controller.getState();

  const paginatedItems = state.GenerateContinueByDateSequence?.paginatedItems;
  const total = paginatedItems?.total ?? 0;
  const pageSize = paginatedItems?.pageSize ?? LIST_LIMIT;
  const pages = Math.ceil(total/pageSize);

  controller.setState({
    GenerateContinueByDateSequence: {
      ...state.GenerateContinueByDateSequence,
      generateContinueData: {
        ...INIT_DATA,
      },
      continueList: [],
      paginatedItems: {
        ...paginatedItems,
        total,
        pageSize,
        page: pages - 1,
      },
    },
  });

  // Re-fetch
  HandleFetchContinue(controller, {} as any);
};

/**========================================================================
 **                               APIs
 *========================================================================**/

const GenDrugContinueLogData: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await GenDrugContinueInterfaceLogList.list({
    apiToken: controller.apiToken,
    params,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp;
};

const GenerateDrugContinue: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await GenDrugContinueInterfaceData.post({
    apiToken: controller.apiToken,
    data: {
      ...params.data,
    },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return [null, err];

  return [resp, err];
};